import React, { useEffect, useRef } from 'react';
import { Check, Lock } from 'lucide-react';
import {
    stepTitle,
    stepDescription,
    stepPhase,
    PHASE_TITLES,
    STEP_ICONS,
    FUNCTIONAL_STEPS,
} from '../constants';

/**
 * Progress tracker for the year-end wizard — a sibling of the VAT
 * stepper's VatProgressTracker (same card, indicators, connectors and
 * pulse), adapted for up to nine steps: the row scrolls horizontally
 * with a styled thin scrollbar and keeps the displayed step in view;
 * on mobile it stacks, exactly like the VAT tracker does.
 * Steps not yet built (P1: everything after "Stäm av kontona") get a
 * lock marker + "Kommer snart" sublabel but stay clickable for preview.
 */
const YearEndProgressTracker = ({
    stepOrder,
    displayedStep,
    isStepCompleted,
    onStepClick,
    processType,
}) => {
    const scrollRef = useRef(null);

    const getStatus = (step) => {
        if (isStepCompleted(step)) return 'completed';
        if (step === displayedStep) return 'current';
        return 'pending';
    };

    // Faserna: consecutive steps sharing a phase form one labeled group —
    // "Bokslutet · Årsredovisningen · Deklarationen · Efter stämman". The
    // grouping is presentational; the chain stays one guided path.
    const phaseGroups = [];
    stepOrder.forEach((step) => {
        const phase = stepPhase(step);
        const last = phaseGroups[phaseGroups.length - 1];
        if (last && last.phase === phase) last.steps.push(step);
        else phaseGroups.push({ phase, steps: [step] });
    });

    const phaseStatus = (group) => {
        if (group.steps.every((s) => isStepCompleted(s))) return 'completed';
        if (group.steps.includes(displayedStep)) return 'active';
        return 'pending';
    };

    // Keep the displayed step visible in the scrollable row (nine steps
    // overflow a 1200px container) — purely presentational.
    useEffect(() => {
        const row = scrollRef.current;
        if (!row) return;
        const el = row.querySelector('.ye-step-block.current');
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        }
    }, [displayedStep]);

    return (
        <>
            <style>{`
                .ye-progress-scroll {
                    padding: 1.5rem;
                    overflow-x: auto;
                    scrollbar-width: thin;
                }
                /* Scroll affordance — the vat-stepper-container scrollbar. */
                .ye-progress-scroll::-webkit-scrollbar { height: 6px; }
                .ye-progress-scroll::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                .ye-progress-scroll::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                .ye-progress-scroll::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }

                .ye-steps-row {
                    display: flex;
                    align-items: stretch;
                    justify-content: space-between;
                }

                /* Fasgrupper — stegen hör till fyra igenkännbara aktiviteter. */
                .ye-phase-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.45rem;
                    flex: 1 0 auto;
                }
                .ye-phase-label {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.3rem;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #9ca3af;
                }
                .ye-phase-group.active .ye-phase-label { color: var(--primary); }
                .ye-phase-group.completed .ye-phase-label { color: #065f46; }
                .ye-phase-steps {
                    display: flex;
                    align-items: center;
                    flex: 1;
                }
                .ye-phase-divider {
                    flex: 0 0 1px;
                    background: #e5e7eb;
                    margin: 0 1.1rem;
                    align-self: stretch;
                }

                .ye-step-block {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    flex: 0 0 auto;
                }

                .ye-step-indicator {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    font-weight: 600;
                    font-size: 0.8rem;
                    transition: all 0.3s ease;
                    flex-shrink: 0;
                }

                .ye-step-indicator.completed {
                    background: var(--bs-success);
                    color: white;
                }

                .ye-step-indicator.current {
                    background: var(--primary);
                    color: white;
                    animation: ye-pulse 2s infinite;
                }

                .ye-step-indicator.pending {
                    background: #f3f4f6;
                    color: #6b7280;
                }

                .ye-step-lock {
                    position: absolute;
                    bottom: -3px;
                    right: -3px;
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 50%;
                    width: 16px;
                    height: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #9ca3af;
                }

                @keyframes ye-pulse {
                    0% { box-shadow: 0 0 0 0 var(--rgba-primary-3); }
                    70% { box-shadow: 0 0 0 10px transparent; }
                    100% { box-shadow: 0 0 0 0 transparent; }
                }

                .ye-step-connector {
                    flex: 1 0 18px;
                    height: 2px;
                    background: #e5e7eb;
                    margin: 0 0.5rem;
                    align-self: center;
                    transition: background 0.3s ease;
                }

                .ye-step-connector.completed {
                    background: var(--primary);
                }

                .ye-step-text { max-width: 140px; }

                .ye-step-label {
                    font-weight: 600;
                    font-size: 0.8rem;
                    color: var(--title, #374151);
                    transition: color 0.2s ease;
                    line-height: 1.2;
                }

                .ye-step-sublabel {
                    font-size: 0.7rem;
                    color: #6b7280;
                    line-height: 1.2;
                    margin-top: 2px;
                }

                .ye-step-block:hover .ye-step-label {
                    color: var(--primary);
                }

                .ye-step-block.completed .ye-step-label { color: #065f46; }
                .ye-step-block.current .ye-step-label { color: var(--primary); }
                .ye-step-block.pending .ye-step-label { color: #6b7280; }
                .ye-step-block.locked .ye-step-label { color: #9ca3af; }
                .ye-step-block.locked .ye-step-sublabel { color: #d1d5db; }

                @media (max-width: 991px) {
                    .ye-steps-row {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 1rem;
                    }

                    .ye-step-connector { display: none; }
                    .ye-phase-divider { display: none; }
                    .ye-phase-steps {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 0.5rem;
                    }

                    .ye-step-block {
                        background: #f9fafb;
                        border-radius: 8px;
                        border: 1px solid #e5e7eb;
                        padding: 0.6rem 0.9rem;
                    }

                    .ye-step-text { max-width: none; }
                }
            `}</style>

            <div className="modern-card">
                <div className="ye-progress-scroll" ref={scrollRef}>
                    <div className="ye-steps-row">
                        {phaseGroups.map((group, gi) => {
                            const gStatus = phaseStatus(group);
                            return (
                                <React.Fragment key={group.phase}>
                                    <div className={`ye-phase-group ${gStatus}`}>
                                        <div className="ye-phase-label">
                                            {gStatus === 'completed' && <Check size={11} />}
                                            {PHASE_TITLES[group.phase]}
                                        </div>
                                        <div className="ye-phase-steps">
                                            {group.steps.map((step, index) => {
                                                const status = getStatus(step);
                                                const locked = !FUNCTIONAL_STEPS.includes(step);
                                                const IconComponent = STEP_ICONS[step];
                                                return (
                                                    <React.Fragment key={step}>
                                                        <div
                                                            className={`ye-step-block ${status} ${locked ? 'locked' : ''}`}
                                                            onClick={() => onStepClick && onStepClick(step)}
                                                        >
                                                            <div className={`ye-step-indicator ${status}`}>
                                                                {status === 'completed' ? (
                                                                    <Check size={18} />
                                                                ) : IconComponent ? (
                                                                    <IconComponent size={16} />
                                                                ) : (
                                                                    stepOrder.indexOf(step) + 1
                                                                )}
                                                                {locked && (
                                                                    <span className="ye-step-lock">
                                                                        <Lock size={9} />
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="ye-step-text ms-2 me-1">
                                                                <div className="ye-step-label">{stepTitle(step, processType)}</div>
                                                                <div className="ye-step-sublabel">
                                                                    {locked ? 'Kommer snart' : stepDescription(step, processType)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {index < group.steps.length - 1 && (
                                                            <div className={`ye-step-connector ${status === 'completed' ? 'completed' : ''}`} />
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {gi < phaseGroups.length - 1 && (
                                        <div className="ye-phase-divider" aria-hidden="true" />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

export default YearEndProgressTracker;
